import React, { useEffect, useContext, useState, useRef } from 'react';
import { MessageContext } from '../../context/MessageContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { HighlightMenu, MenuButton } from 'react-highlight-menu'
import {
  generateVariableQuestions,
  questionPatterns,
  defaultQuestions,
  generateErrorQuestions,
} from '../../component/student-components/data/CodeQuestions';
import { AuthContext } from '../../context/AuthContext';
import { SelectedCodeContext } from '../../context/SelectedCodeContext';

const PythonInterpreter = ({ output }) => {
  const outputRef = useRef();
  const { setChatMessage } = useContext(MessageContext);
  const { selectedCode, setSelectedCode } = useContext(SelectedCodeContext)

  // Declare states
  const [codeText, setCodeText] = useState('');
  const [open, setOpen] = useState(false);
  const [textField, setTextField] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);
  const [questionList, setQuestionList] = useState([]);
  const [customQuestion, setCustomQuestion] = useState('');
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const { currentUser } = useContext(AuthContext)

  const handleOpen = (selectedText, setMenuOpen) => {
    setCodeText(selectedText);

    const generatedQuestions = generateQuestions(selectedText);
    setQuestionList(generatedQuestions);

    setOpen(true);
    setMenuOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    setTextField(false);
    setDisabledButton(false);
  };

  const handleSetMessage = (message, output) => {
      const formattedMessage = `This is the output I want you to focus on:\n${output.trim()}\n\n${message}`;
      
      // Set the message in the textarea
      const textArea = document.getElementById("myTextArea");
      if (textArea) {
          textArea.value = formattedMessage;
      }
      
      // Trigger the chat message update
      setChatMessage(formattedMessage);
      
      setOpen(false);
      
      // Auto-trigger the send - simulate clicking the send button
      setTimeout(() => {
          const sendButton = document.querySelector('.send-button');
          if (sendButton && !sendButton.disabled) {
              sendButton.click();
          }
      }, 100);
  };

  // Handle send button in dialog
  const handleSendFromDialog = () => {
      if (customQuestion.trim()) {
          handleSetMessage(customQuestion, codeText);
          setCustomQuestion(''); // Clear after sending
      }
  };

  const generateQuestions = (errorMessage) => {
      const errorQuestions = generateErrorQuestions(errorMessage);
  
      return errorQuestions.length > 0 ? errorQuestions : defaultQuestions;
  };
  

  return (
    <>
      {(currentUser.role === 3 || currentUser.role === 4) && (
        <HighlightMenu
          styles={{
            borderColor: '#1976d2',
            backgroundColor: 'white',
            boxShadow: '0px 5px 5px 0px rgba(21, 82, 146, 0.5)',
            zIndex: 10,
            borderRadius: '5px',
            padding: '3px',
          }}
          target={outputRef}
          menu={({ selectedText, setMenuOpen, setClipboard }) => (
            <>
              <IconButton onClick={() => handleOpen(selectedText, setMenuOpen)}>
              <QuestionMarkIcon sx={{ color: "#1976d2"}} />
              </IconButton>
            </>
          )}
        />
      )}
      <pre 
        ref={outputRef} 
        className="codeOutputArea" 
        style={{
          whiteSpace: 'pre-wrap',
          fontFamily: 'monospace',
          margin: 0,
          padding: '8px',
          overflow: 'auto'
        }}
      >
        {output}
      </pre>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
    >
        <DialogTitle className="dialog-title" id="alert-dialog-title">
            {'Ask a Question'}
            <CloseIcon className="close-icon" onClick={handleClose} />
        </DialogTitle>
        <DialogContent>
            <DialogContentText id="alert-dialog-description">
                <pre className="code-editor-style">{codeText}</pre>
                <List>
                    {questionList.map((text, index) => (
                        <ListItem
                            disablePadding
                            key={index}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                        >
                            <ListItemButton 
                                className="list-item-button" 
                                onClick={() => setCustomQuestion(text)}
                            >
                                <ListItemText primary={text} className="list-item-text" />
                                <ListItemIcon className="list-item-icon">
                                    <CheckIcon className={`check-icon ${hoveredIndex === index ? 'hovered' : 'default'}`} />
                                </ListItemIcon>
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
                <div style={{ marginTop: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <TextField
                        fullWidth
                        label="Edit or write your question"
                        variant="outlined"
                        value={customQuestion}
                        onChange={(e) => setCustomQuestion(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendFromDialog();
                            }
                        }}
                        placeholder="Click a suggestion above or type your own"
                    />
                    <IconButton 
                        onClick={handleSendFromDialog}
                        disabled={!customQuestion.trim()}
                        color="primary"
                    >
                        <DoneIcon />
                    </IconButton>
                </div>
            </DialogContentText>
        </DialogContent>
    </Dialog>
    </>
  );
};

export default PythonInterpreter;
